import { useMutation, useQueryClient } from '@tanstack/react-query';

import { CALENDAR_EVENTS_QUERY_KEY } from '@/app/(main)/calendar/consts/query-keys';
import { CALENDAR_TASK_STATUS } from '@/app/(main)/calendar/consts/calendar-task-status';
import { applyTaskStatusOptimisticUpdate } from '@/app/(main)/calendar/utils/applyTaskStatusOptimisticUpdate';
import { useFetch } from '@/hooks/useFetch';
import type { ErrorResponse } from '@/types/apiTypes';
import type { CalendarEvent } from '@/types/calendarTypes';
import type { UpdateClientTaskRequest, UpdateClientTaskResponse } from '@/types/clientTasksApiTypes';

type TaskStatusMutationContext = {
  previousQueries: Array<[queryKey: readonly unknown[], data: CalendarEvent[] | undefined]>;
};

export function useUpdateCalendarTaskStatusMutation() {
  const queryClient = useQueryClient();
  const { doFetch } = useFetch<UpdateClientTaskRequest, UpdateClientTaskResponse>();

  return useMutation<
    UpdateClientTaskResponse['data'],
    Error,
    { taskId: string; completed: boolean },
    TaskStatusMutationContext
  >({
    mutationFn: async ({ taskId, completed }) => {
      const response = await doFetch(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        body: {
          status: completed ? CALENDAR_TASK_STATUS.COMPLETED : CALENDAR_TASK_STATUS.OPEN,
        },
      });
      return response.data;
    },
    onMutate: async ({ taskId, completed }) => {
      await queryClient.cancelQueries({ queryKey: [CALENDAR_EVENTS_QUERY_KEY] });

      const previousQueries: TaskStatusMutationContext['previousQueries'] = [];

      queryClient
        .getQueriesData<CalendarEvent[]>({ queryKey: [CALENDAR_EVENTS_QUERY_KEY] })
        .forEach(([queryKey, data]) => {
          previousQueries.push([queryKey, data]);
          if (data) {
            queryClient.setQueryData(queryKey, applyTaskStatusOptimisticUpdate(data, taskId, completed));
          }
        });

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [CALENDAR_EVENTS_QUERY_KEY] });
    },
  });
}

export function useUpdateCalendarTaskMutation() {
  const queryClient = useQueryClient();
  const { doFetch } = useFetch<UpdateClientTaskRequest, UpdateClientTaskResponse>();

  return useMutation<
    UpdateClientTaskResponse['data'],
    ErrorResponse<UpdateClientTaskRequest>,
    { taskId: number; data: UpdateClientTaskRequest }
  >({
    mutationFn: async ({ taskId, data }: { taskId: number; data: UpdateClientTaskRequest }) => {
      const response = await doFetch(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CALENDAR_EVENTS_QUERY_KEY] });
    },
  });
}
